/**
 * The browser web-photo policy: never upscale, cap the long edge at 1600px,
 * encode as JPEG at quality 0.82, and keep the prepared copy within the
 * existing 20 MiB image upload limit. The encode API has no progress callback,
 * so callers must present preparation as an indeterminate phase.
 */
export const BROWSER_IMAGE_POLICY = {
  inputMimeTypes: ['image/jpeg', 'image/png', 'image/webp'] as const,
  outputMimeType: 'image/jpeg' as const,
  outputExtension: '.jpg' as const,
  maxLongEdge: 1600,
  quality: 0.82,
  maxBytes: 20 * 1024 * 1024,
} as const

export type BrowserImageInputMimeType = typeof BROWSER_IMAGE_POLICY.inputMimeTypes[number]

export type BrowserImagePreparationReason =
  | 'unsupported_type'
  | 'decode_failed'
  | 'invalid_dimensions'
  | 'canvas_failed'
  | 'encode_failed'
  | 'unexpected_output_mime'
  | 'output_dimensions_invalid'
  | 'output_size_exceeded'
  | 'file_construction_failed'

export type BrowserImagePreparationFailure = {
  readonly ok: false
  readonly reason: BrowserImagePreparationReason
  readonly message: string
}

export type PreparedBrowserImage = {
  readonly ok: true
  readonly file: File
  readonly width: number
  readonly height: number
  readonly size: number
}

export type BrowserImagePreparation = PreparedBrowserImage | BrowserImagePreparationFailure

const REFUSAL_MESSAGES: Readonly<Record<BrowserImagePreparationReason, string>> = {
  unsupported_type: 'Формат фото не поддерживается. Нужны JPG, PNG или WEBP.',
  decode_failed: 'Фото не удалось прочитать в браузере. Оригинал не отправлен.',
  invalid_dimensions: 'У фото не удалось определить корректные размеры. Оригинал не отправлен.',
  canvas_failed: 'Браузер не смог подготовить фото на холсте. Оригинал не отправлен.',
  encode_failed: 'Браузер не смог сжать фото. Оригинал не отправлен.',
  unexpected_output_mime: 'Браузер создал фото в неожиданном формате. Оригинал не отправлен.',
  output_dimensions_invalid: 'Подготовленная копия фото имеет недопустимые размеры. Оригинал не отправлен.',
  output_size_exceeded: 'Подготовленная копия фото больше 20 МБ. Оригинал не отправлен.',
  file_construction_failed: 'Подготовленную копию фото не удалось создать. Оригинал не отправлен.',
}

export function isSupportedBrowserImageMimeType(value: string): value is BrowserImageInputMimeType {
  return (BROWSER_IMAGE_POLICY.inputMimeTypes as readonly string[]).includes(value)
}

function failure(reason: BrowserImagePreparationReason): BrowserImagePreparationFailure {
  return { ok: false, reason, message: REFUSAL_MESSAGES[reason] }
}

function hasValidDimensions(width: number, height: number): boolean {
  return Number.isFinite(width)
    && Number.isFinite(height)
    && Number.isInteger(width)
    && Number.isInteger(height)
    && width > 0
    && height > 0
}

function preparedFileName(name: string): string {
  const stem = name.replace(/\.[^/.]*$/, '') || 'photo'
  return `${stem}${BROWSER_IMAGE_POLICY.outputExtension}`
}

function encodeCanvas(canvas: HTMLCanvasElement): Promise<Blob | null> {
  return new Promise((resolve) => {
    try {
      canvas.toBlob(resolve, BROWSER_IMAGE_POLICY.outputMimeType, BROWSER_IMAGE_POLICY.quality)
    } catch {
      resolve(null)
    }
  })
}

/** Decode, bound, and encode one selected photograph without an original fallback. */
export async function prepareBrowserImage(file: File): Promise<BrowserImagePreparation> {
  if (!isSupportedBrowserImageMimeType(file.type.split(';', 1)[0]?.trim().toLowerCase() ?? '')) {
    return failure('unsupported_type')
  }

  let decoded: ImageBitmap
  try {
    if (typeof createImageBitmap !== 'function') return failure('decode_failed')
    decoded = await createImageBitmap(file)
  } catch {
    return failure('decode_failed')
  }

  if (!hasValidDimensions(decoded.width, decoded.height)) {
    decoded.close()
    return failure('invalid_dimensions')
  }

  const scale = Math.min(1, BROWSER_IMAGE_POLICY.maxLongEdge / Math.max(decoded.width, decoded.height))
  const width = Math.max(1, Math.round(decoded.width * scale))
  const height = Math.max(1, Math.round(decoded.height * scale))
  if (!hasValidDimensions(width, height) || Math.max(width, height) > BROWSER_IMAGE_POLICY.maxLongEdge) {
    decoded.close()
    return failure('output_dimensions_invalid')
  }

  let canvas: HTMLCanvasElement
  let context: CanvasRenderingContext2D | null
  try {
    canvas = document.createElement('canvas')
    canvas.width = width
    canvas.height = height
    context = canvas.getContext('2d')
    if (context === null) return failure('canvas_failed')
    context.drawImage(decoded, 0, 0, width, height)
  } catch {
    return failure('canvas_failed')
  } finally {
    decoded.close()
  }

  const encoded = await encodeCanvas(canvas)
  if (encoded === null || encoded.size === 0) return failure('encode_failed')
  if (encoded.type.toLowerCase() !== BROWSER_IMAGE_POLICY.outputMimeType) return failure('unexpected_output_mime')
  if (encoded.size > BROWSER_IMAGE_POLICY.maxBytes) return failure('output_size_exceeded')

  let preparedDimensions: ImageBitmap
  try {
    if (typeof createImageBitmap !== 'function') return failure('output_dimensions_invalid')
    preparedDimensions = await createImageBitmap(encoded)
  } catch {
    return failure('output_dimensions_invalid')
  }
  const validOutput = hasValidDimensions(preparedDimensions.width, preparedDimensions.height)
    && preparedDimensions.width === width
    && preparedDimensions.height === height
    && Math.max(preparedDimensions.width, preparedDimensions.height) <= BROWSER_IMAGE_POLICY.maxLongEdge
  preparedDimensions.close()
  if (!validOutput) return failure('output_dimensions_invalid')

  try {
    const prepared = new File([encoded], preparedFileName(file.name), {
      type: BROWSER_IMAGE_POLICY.outputMimeType,
      lastModified: file.lastModified,
    })
    if (prepared.type !== BROWSER_IMAGE_POLICY.outputMimeType || prepared.size !== encoded.size) {
      return failure('file_construction_failed')
    }
    return { ok: true, file: prepared, width, height, size: prepared.size }
  } catch {
    return failure('file_construction_failed')
  }
}
